import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { SendToUnsubscribedValues, SetPreferencesType } from 'cio/utils/subscription-center';
import SubscriptionCenterService, { MessageListFilterParams } from 'cio/services/subscription-center';
import { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { DS } from 'ember-data';

export default class SubscriptionCenterSetPreferencesTypeController extends Controller {
  @service subscriptionCenter!: SubscriptionCenterService;
  @service store!: DS.Store;

  @tracked type: SetPreferencesType = SetPreferencesType.Pending;
  @tracked page = 1;
  @tracked searchString: string | null = null;
  @tracked searchTagIds: string[] = [];
  @tracked searchTopicId: string | null = null;
  @tracked searchType: string | null = null;
  @tracked searchAudience: string | null = null;
  @tracked sendToUnsubscribed: null | SendToUnsubscribedValues = null;

  readonly availableTypes = [
    {
      label: 'Campaigns',
      value: 'campaign',
    },
    {
      label: 'Newsletters',
      value: 'newsletter',
    },
    {
      label: 'API triggered broadcast',
      value: 'api_triggered_broadcast',
    },
  ];

  get subscriptionTopics() {
    if (this.subscriptionCenter.initialized) {
      return this.subscriptionCenter.topics;
    }

    return [];
  }

  get activeMessages() {
    switch (this.type) {
      case SetPreferencesType.Pending:
        return this.pendingMessages;
      case SetPreferencesType.Completed:
        return this.completedMessages;
      default:
        return this.allMessages;
    }
  }

  get pendingMessages() {
    return this.subscriptionCenter.pendingMessages;
  }

  get completedMessages() {
    return this.subscriptionCenter.completedMessages;
  }

  get allMessages() {
    return this.subscriptionCenter.allMessages;
  }

  get isLoading() {
    return (
      this.subscriptionCenter.fetchPendingMessageLists.isRunning ||
      this.subscriptionCenter.fetchCompletedMessageLists.isRunning ||
      this.subscriptionCenter.fetchAllMessageLists.isRunning
    );
  }

  get searchParams(): MessageListFilterParams {
    return {
      page: this.page,
      searchStr: this.searchString,
      tagIds: this.searchTagIds,
      topicId: this.searchTopicId,
      type: this.searchType,
      sendToUnsubs: this.sendToUnsubscribed,
    };
  }

  get filterEnabled() {
    return [
      this.searchAudience,
      this.searchString,
      this.searchTagIds,
      this.searchTopicId,
      this.searchType,
      this.sendToUnsubscribed,
    ].some((param) => isPresent(param));
  }

  @action
  setActiveTab(type: SetPreferencesType) {
    this.type = type;
  }

  @action
  clearAudienceSearch() {
    this.sendToUnsubscribed = null;
    this.searchTopicId = null;
    this.#fetchAllLists();
  }

  @action
  updateQuery(
    key: 'page' | 'searchString' | 'searchTagIds' | 'searchTopicId' | 'searchType' | 'sendToUnsubscribed',
    value: number | string | string[] | null,
  ) {
    (this[key] as any) = value;
    switch (key) {
      case 'page':
        this.page = value as number;
        break;
      case 'searchString':
        this.searchString = value as string;
        break;
      case 'searchTagIds':
        this.searchTagIds = value as string[];
        break;
      case 'searchTopicId':
        this.searchTopicId = value as string;
        break;
      case 'searchType':
        this.searchType = value as string;
        break;
      case 'sendToUnsubscribed':
        this.searchTopicId = null;
        this.sendToUnsubscribed = value as SendToUnsubscribedValues;
        break;
    }

    if (key === 'page') {
      this.#requestNewPage();
      return;
    }
    this.#fetchAllLists();
  }

  #fetchAllLists() {
    this.subscriptionCenter.fetchPendingMessageLists.perform(this.searchParams);
    this.subscriptionCenter.fetchCompletedMessageLists.perform(this.searchParams);
    this.subscriptionCenter.fetchAllMessageLists.perform(this.searchParams);
  }

  #requestNewPage() {
    let params = { ...this.searchParams, page: this.page };
    switch (this.type) {
      case SetPreferencesType.Pending:
        return this.subscriptionCenter.fetchPendingMessageLists.perform(params);
      case SetPreferencesType.Completed:
        return this.subscriptionCenter.fetchCompletedMessageLists.perform(params);
      default:
        return this.subscriptionCenter.fetchAllMessageLists.perform(params);
    }
  }
}
